import type { TSession } from "@/types/TSession";
import {
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { closeSession } from "@/services/heygen/dashboard/closeSession";
import { useState } from "react";
import { getSessionColumns } from "@/components/dashboard/heygen-account/sessions-info/table/sessionColumns";

export function useSessionsTable(data: TSession[], onRefresh?: () => void) {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [sessionToClose, setSessionToClose] = useState<TSession | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const handleCloseSession = async (sessionId: string) => {
    try {
      setIsLoading(sessionId);
      await closeSession(sessionId);
      if (onRefresh) {
        // Add a small delay to allow the server to update the session status
        setTimeout(() => {
          onRefresh();
        }, 1000);
      }
    } catch (error) {
      console.error("Failed to close session:", error);
    } finally {
      setIsLoading(null);
    }
  };

  const openConfirmDialog = (session: TSession) => {
    setSessionToClose(session);
    setIsConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setSessionToClose(null);
  };

  const columns = getSessionColumns(openConfirmDialog, isLoading);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 0,
      maxSize: 1000,
    },
  });

  return {
    table,
    sessionToClose,
    isConfirmDialogOpen,
    handleCloseSession,
    closeConfirmDialog,
  };
}